/**
 * Minimal i18n runtime for the www landing site.
 *
 * - Supported locales: en (canonical), es, hi, zh.
 * - Locale is resolved once from `navigator.language` (or `?lang=` override)
 *   and cached. `t("key")` returns the localized string, falling back to
 *   the English message, and finally to the key itself.
 * - No runtime dependencies; framework-agnostic (works inside React via
 *   `useState(getLocale())` + `setLocale()` listener pattern).
 */

import { en, type MessageKey } from "./locales/en";
import { es } from "./locales/es";
import { hi } from "./locales/hi";
import { zh } from "./locales/zh";

export type Locale = "en" | "es" | "hi" | "zh";

const TABLES: Record<Locale, Partial<Record<MessageKey, string>>> = {
  en,
  es,
  hi,
  zh,
};

const SUPPORTED: readonly Locale[] = ["en", "es", "hi", "zh"];
const STORAGE_KEY = "kc-locale";

let current: Locale | null = null;
const listeners = new Set<(l: Locale) => void>();

function detect(): Locale {
  if (typeof window === "undefined") return "en";
  // URL override wins.
  try {
    const qs = new URLSearchParams(window.location.search).get("lang");
    if (qs && SUPPORTED.includes(qs as Locale)) return qs as Locale;
  } catch {
    /* empty */
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored as Locale)) return stored as Locale;
  } catch {
    /* storage disabled */
  }
  const nav = (window.navigator.language || "en").slice(0, 2).toLowerCase();
  return (SUPPORTED as readonly string[]).includes(nav)
    ? (nav as Locale)
    : "en";
}

export function getLocale(): Locale {
  if (current === null) current = detect();
  return current;
}

export function setLocale(locale: Locale): void {
  if (!SUPPORTED.includes(locale)) return;
  current = locale;
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }
  } catch {
    /* storage disabled */
  }
  for (const fn of listeners) fn(locale);
}

export function subscribe(fn: (l: Locale) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Translate a message key. Falls back to the English string, then the key.
 */
export function t(key: MessageKey, locale: Locale = getLocale()): string {
  return TABLES[locale]?.[key] ?? en[key] ?? key;
}

/** Reset all cached locale state — test-only helper. */
export function resetForTesting(): void {
  current = null;
  listeners.clear();
}

export const supportedLocales = SUPPORTED;
